import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Chip } from '@/components/kit';
import apiClient from '@/services/apiClient';
import type { MemberRow } from '../lib/useManagerData';

/**
 * The two account-card rows `g_set_roles` / `g_t6` draw under the roles:
 *
 *  - WHEN AWAY, RESULTS GO TO — the covering doctor, picked from the clinic's
 *    active doctors. Only a doctor has one.
 *  - PRINTED IDENTITY — title, degrees and syndicate number, printed on that
 *    person's documents. The signature itself is the doctor's own image; this
 *    row only says whether it exists.
 *
 * One `PATCH /tenants/:id/members/:userId/profile`.
 */
export function MemberProfileCards({ member, doctors, tenantId, onNote }: {
  member: MemberRow;
  doctors: MemberRow[];
  tenantId: string | null;
  onNote: (n: { kind: 'ok' | 'bad'; text: string }) => void;
}) {
  const qc = useQueryClient();
  const [cover, setCover] = useState(member.coveringDoctorId ?? '');
  const [title, setTitle] = useState(member.title ?? '');
  const [degrees, setDegrees] = useState(member.degrees ?? '');
  const [syndicate, setSyndicate] = useState(member.syndicateNo ?? '');

  useEffect(() => {
    setCover(member.coveringDoctorId ?? '');
    setTitle(member.title ?? '');
    setDegrees(member.degrees ?? '');
    setSyndicate(member.syndicateNo ?? '');
  }, [member.userId, member.coveringDoctorId, member.title, member.degrees, member.syndicateNo]);

  const save = useMutation({
    mutationFn: async (body: Record<string, string | null>) => {
      await apiClient.patch(`/tenants/${tenantId}/members/${member.userId}/profile`, body);
    },
    onSuccess: () => {
      onNote({ kind: 'ok', text: 'Saved.' });
      void qc.invalidateQueries({ queryKey: ['cm-members', tenantId] });
    },
    onError: () => onNote({ kind: 'bad', text: 'That was refused. Nothing has changed.' }),
  });

  const isDoctor = member.roleName === 'Doctor';
  const covers = doctors.filter((d) => d.userId !== member.userId && d.status === 'active');
  const dirty =
    title !== (member.title ?? '') ||
    degrees !== (member.degrees ?? '') ||
    syndicate !== (member.syndicateNo ?? '');

  return (
    <>
      {isDoctor ? (
        <Card>
          <p className="t-ovl mut mb8">When away, results go to</p>
          <select className="cm-inp" aria-label="Covering doctor" value={cover}
            disabled={save.isPending}
            onChange={(e) => {
              setCover(e.target.value);
              save.mutate({ coveringDoctorId: e.target.value || null });
            }}>
            <option value="">No one — results wait for them</option>
            {covers.map((d) => (
              <option key={d.userId} value={d.userId}>
                {[d.firstName, d.lastName].filter(Boolean).join(' ') || d.email}
              </option>
            ))}
          </select>
          <p className="t-cap mut mt8">
            Results and clinical messages only — never bills or payouts.
          </p>
        </Card>
      ) : null}

      <Card>
        <p className="t-ovl mut mb8">Printed identity</p>
        <label className="t-cap mut" htmlFor="cm-pi-synd">Syndicate number</label>
        <input id="cm-pi-synd" className="cm-inp" value={syndicate} placeholder="123456"
          onChange={(e) => setSyndicate(e.target.value)} />
        <label className="t-cap mut mt8" htmlFor="cm-pi-title">Title</label>
        <input id="cm-pi-title" className="cm-inp" value={title} placeholder="Consultant"
          onChange={(e) => setTitle(e.target.value)} />
        <label className="t-cap mut mt8" htmlFor="cm-pi-deg">Degrees</label>
        <input id="cm-pi-deg" className="cm-inp" value={degrees} placeholder="MD, FRCS(Ophth)"
          onChange={(e) => setDegrees(e.target.value)} />
        <div className="flex mt8" style={{ gap: 8, alignItems: 'center' }}>
          <Chip kind={member.hasSignature ? 'success' : 'outline'} small>
            {member.hasSignature ? 'signature & stamp ✓ set by the doctor' : 'no signature on file'}
          </Chip>
          <span className="grow" />
          <button type="button" className="btn btn-primary" disabled={!dirty || save.isPending}
            onClick={() => save.mutate({
              syndicateNo: syndicate.trim() || null,
              title: title.trim() || null,
              degrees: degrees.trim() || null,
            })}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
        <p className="t-cap mut mt8">
          The signature image is the doctor’s own — this row only says it exists.
        </p>
      </Card>
    </>
  );
}
